///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PlannerEulaDTO } from '../../../../generated/shared-types.model';
import { acceptEula, checkAcceptEula, closeEulaUpdateDialog, getEula, setEula, uploadEula } from '../eula.actions';
import { concatMap, finalize, map, switchMap, tap } from 'rxjs/operators';
import { LoaderIds, PreloaderService } from '../../../../services/preloader.service';
import { getUsersPage } from '../../../../users/store/users-list.actions';
import { Store } from '@ngrx/store';
import { selectEula } from '../eula.selectors';
import { of } from 'rxjs';
import { isDefined } from '@siklu/sui-lib/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { GtthEulaService } from '../../../../global-dialogs/eula/service/eula.service';

@Injectable()
export class EulaEffects {
    getEula$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(getEula),
            concatLatestFrom(() => this.store.select(selectEula)),
            // if EULA doesn't exist backend will respond with empty object, so it is safe to check object existence
            switchMap(([, eula]) => (isDefined(eula) ? of(eula) : this.eulaService.getEula())),
            map((eula: PlannerEulaDTO) => setEula({ eula }))
        );
    });

    uploadEula$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(uploadEula),
            tap(() => this.preloader.show(LoaderIds.UPDATE_EULA_ID)),
            switchMap((action) =>
                this.eulaService.updateEula(action.file).pipe(finalize(() => this.preloader.hide(LoaderIds.UPDATE_EULA_ID)))
            ),
            concatMap((eula: PlannerEulaDTO) => {
                return [setEula({ eula }), closeEulaUpdateDialog(), getUsersPage()];
            })
        );
    });

    acceptEula$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(acceptEula),
            tap(() => this.preloader.show(LoaderIds.ACCEPT_EULA_ID)),
            switchMap(() => this.eulaService.acceptNewEula().pipe(finalize(() => this.preloader.hide(LoaderIds.ACCEPT_EULA_ID)))),
            map((isAccept: boolean) => checkAcceptEula({ isAccept }))
        );
    });

    constructor(
        private readonly actions$: Actions,
        private readonly preloader: PreloaderService,
        private readonly eulaService: GtthEulaService,
        private readonly store: Store
    ) {}
}
